import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ConversionService } from '../conversion.service';
import {
	conversionExceptions,
	downloadExceptionsCsv,
	downloadSkippedInvoicesCsv,
	readDownloadError,
	saveDownloadedFile,
	skippedNegativeInvoices,
} from '../skipped-invoices-csv';
import { ConversionClient } from '../models/ConversionClient';
import { ConversionMigrationProgress } from '../models/ConversionMigrationProgress';
import {
	ConversionMigrationResult,
	ConversionMigrationStep,
} from '../models/ConversionMigrationResult';

/** One finished carrier in a batch, listed under the current carrier's progress. */
export class MigrationBatchEntry {
	clientName: string;
	/** Completed, Completed with problems or Failed. */
	status: string;
	summary: string;
}

export class MigrationProgressStepRow {
	step: number;
	action: string;
	/** Pending, Running, Success, Failed, Skipped or Not Run. */
	status: string;
	detail: string;
	durationMs: number;
}

/**
 * Live view of a migration. Polls Migrate/Progress once a second while the Migrate request
 * is open, then shows the final per-step result. In a batch the modal stays open: each
 * carrier takes over the step table in turn, and the finished ones are listed below it.
 */
@Component({
	selector: 'app-migration-progress-modal',
	templateUrl: './migration-progress-modal.component.html',
	styleUrls: ['./migration-progress-modal.component.scss'],
})
export class MigrationProgressModalComponent implements OnDestroy {
	@ViewChild('MigrationProgressModalTemplate')
	MigrationProgressModalTemplate: ElementRef;

	public clientName = '';
	public progress: ConversionMigrationProgress;
	public stepRows: MigrationProgressStepRow[] = [];
	public isRunning = false;
	public requestError = '';
	public elapsedMs = 0;
	/** 1 = Part 1 (steps 1-11), 2 = Part 2 (steps 12-23), 3 = the full conversion (steps 1-23). */
	public part = 1;

	/** Position of the current carrier in the batch (1-based), and the batch size. */
	public batchIndex = 1;
	public batchTotal = 1;
	/** Carriers already finished in this batch, oldest first. */
	public batchEntries: MigrationBatchEntry[] = [];

	/** Mirrors ConversionMigrationStepNames on the server. Index 0 is step 1. */
	private readonly allStepNames = [
		'Import the client with transfer terms',
		'Set the client doNotBill to true',
		'Set the client ACH and wire fees',
		'Import debtors not existing in Factor Cloud',
		'Import client contacts',
		'Import debtor contacts',
		'Map the client/debtor relationships',
		'Import client notes',
		'Import customer notes',
		'Upload client documents to FactorCloud',
		'Upload customer documents to FactorCloud',
		'Create invoice export files',
		'Create a $0 Invoice for FirstInvoiceDate',
		'Import invoices',
		'Put the imported invoices on the transfer term',
		'Sync the imported invoices back to Aladdin',
		'Approve the imported invoices for funding',
		'Import invoice notes',
		'Reset the default funding instruction',
		'Put the client on its live term',
		'Update the client dates and balance locally',
		'Deactivate the WinFactor client in the portal',
		'Set the client emails and turn billing on',
	];
	private readonly part1LastStep = 11;

	private runId = '';
	private startedAt = 0;
	private finishedAt = 0;
	private progressReceivedAt = 0;
	private timer: any;
	private pollInFlight = false;
	private modalRef: NgbModalRef;

	constructor(
		private conversionService: ConversionService,
		private modalService: NgbModal
	) {}

	ngOnDestroy() {
		this.stopTimer();
	}

	/**
	 * Starts showing one carrier. batchIndex 1 opens the modal; later carriers in the same
	 * batch reuse the open modal and just take over the step table.
	 */
	public open(row: ConversionClient, runId: string, part: number, batchIndex?: number, batchTotal?: number) {
		const index = batchIndex && batchIndex > 0 ? batchIndex : 1;
		const reuseOpenModal = index > 1 && !!this.modalRef;

		this.batchIndex = index;
		this.batchTotal = batchTotal && batchTotal > 0 ? batchTotal : 1;
		if (!reuseOpenModal) {
			this.batchEntries = [];
		}

		this.clientName = row ? row.clientName : '';
		this.runId = runId;
		this.part = part === 2 || part === 3 ? part : 1;
		this.progress = null;
		this.requestError = '';
		this.isRunning = true;
		this.startedAt = Date.now();
		this.finishedAt = 0;
		this.elapsedMs = 0;
		this.buildStepRows();

		if (!reuseOpenModal) {
			this.modalRef = this.modalService.open(this.MigrationProgressModalTemplate, {
				container: '.app',
				size: <any>'xl',
				backdrop: 'static',
				keyboard: false,
			});
		}

		this.stopTimer();
		this.timer = setInterval(() => this.tick(), 1000);
	}

	/**
	 * Called when the Migrate request itself returns, successfully or not. Mid-batch
	 * (isLast false) the carrier is only recorded in the batch list: the next carrier's
	 * open() takes over the step table straight away.
	 */
	public requestFinished(result: ConversionMigrationResult, errorMessage: string, isLast?: boolean) {
		this.requestError = errorMessage || '';
		this.finishedAt = Date.now();
		this.elapsedMs = this.finishedAt - this.startedAt;

		if (this.batchTotal > 1) {
			const entry = new MigrationBatchEntry();
			entry.clientName = (result && result.clientName) || this.clientName;
			entry.status = result
				? result.ignored
					? 'Ignored (already in FactorCloud)'
					: result.successful
					? 'Completed'
					: 'Completed with problems'
				: 'Failed';
			entry.summary = (result && result.summary) || errorMessage || '';
			this.batchEntries.push(entry);
		}

		if (isLast === false) {
			// This carrier's run is over: stop polling it now, so a poll still in flight cannot
			// paint its steps over the next carrier's (open() resets these again anyway).
			this.isRunning = false;
			this.stopTimer();
			return;
		}

		// One last read picks up the final snapshot. If the server never registered the
		// run (a request that failed before it started), the returned result stands in.
		this.conversionService
			.getMigrationProgress(this.runId)
			.toPromise()
			.then(
				(progress: ConversionMigrationProgress) => {
					if (progress) {
						this.applyProgress(progress);
					} else if (result) {
						this.applyResult(result);
					}
					this.complete();
				},
				() => {
					if (result) {
						this.applyResult(result);
					}
					this.complete();
				}
			);
	}

	public close() {
		if (this.isRunning) {
			return;
		}
		this.stopTimer();
		if (this.modalRef) {
			this.modalRef.close();
			this.modalRef = null;
		}
	}

	public batchStatusClass(status: string): string {
		switch (status) {
			case 'Completed':
				return 'text-success';
			case 'Failed':
				return 'text-danger font-weight-bold';
			default:
				return 'text-warning font-weight-bold';
		}
	}

	public batchStatusIcon(status: string): string {
		switch (status) {
			case 'Completed':
				return 'fa-circle-check';
			case 'Failed':
				return 'fa-circle-xmark';
			default:
				return 'fa-triangle-exclamation';
		}
	}

	public get overallStatus(): string {
		if (this.isRunning) {
			return 'Running';
		}
		if (this.requestError && !this.hasResult) {
			return 'Failed';
		}
		const result = this.progress ? this.progress.result : null;
		if (result && result.successful) {
			return 'Completed';
		}
		return 'Completed with problems';
	}

	public get overallStatusClass(): string {
		switch (this.overallStatus) {
			case 'Running':
				return 'badge-primary';
			case 'Completed':
				return 'badge-success';
			case 'Failed':
				return 'badge-danger';
			default:
				return 'badge-warning';
		}
	}

	public get summary(): string {
		if (this.progress && this.progress.result && this.progress.result.summary) {
			return this.progress.result.summary;
		}
		if (this.requestError) {
			return this.requestError;
		}
		return this.progress && this.progress.errorMessage ? this.progress.errorMessage : '';
	}

	public get stepsFinished(): number {
		return this.stepRows.filter(
			(row) => row.status === 'Success' || row.status === 'Failed' || row.status === 'Skipped'
		).length;
	}

	public get overallPercent(): number {
		if (!this.isRunning) {
			return 100;
		}
		return Math.round((this.stepsFinished / this.stepRows.length) * 100);
	}

	public get itemPercent(): number {
		if (!this.progress || !this.progress.total) {
			return 0;
		}
		return Math.round(((this.progress.completed || 0) / this.progress.total) * 100);
	}

	public get successCount(): number {
		return this.stepRows.filter((row) => row.status === 'Success').length;
	}

	public get failedCount(): number {
		return this.stepRows.filter((row) => row.status === 'Failed').length;
	}

	public get skippedCount(): number {
		return this.stepRows.filter((row) => row.status === 'Skipped').length;
	}

	/** Negative invoices this carrier's run skipped (not posted to FactorCloud). */
	public get skippedInvoiceCount(): number {
		return skippedNegativeInvoices(this.progress && this.progress.result ? [this.progress.result] : []).length;
	}

	/** Exceptions this carrier's run worked around (e.g. an invalid EIN that was skipped). */
	public get exceptionCount(): number {
		return conversionExceptions(this.progress && this.progress.result ? [this.progress.result] : []).length;
	}

	/** Downloads this carrier's exception file. */
	public downloadExceptions() {
		const result = this.progress ? this.progress.result : null;
		if (result) {
			downloadExceptionsCsv(conversionExceptions([result]), result.clientName || this.clientName);
		}
	}

	/** Step 14: FactorCloud's failed rows of this carrier's invoice CSV import, one file per import. */
	public get invoiceImportErrorFiles(): string[] {
		const result = this.progress ? this.progress.result : null;
		return (result && result.invoiceImportErrorFiles) || [];
	}

	/** True for the export summary report (step 12) among the invoice files offered for download. */
	public isSummaryFile(fileName: string): boolean {
		return !!fileName && /_summary\.csv$/i.test(fileName);
	}

	/** What an invoice file offered for download is: the export summary, the invoices not sent, or the import errors. */
	public invoiceFileLabel(fileName: string): string {
		if (this.isSummaryFile(fileName)) {
			return 'export summary';
		}
		return fileName && fileName.indexOf('_not_sent_') >= 0 ? 'invoices not sent' : 'invoice import errors';
	}

	/** File being downloaded, so its button shows a spinner. */
	public downloadingErrorFile: string = null;

	/** Why the last error file download failed (shown under the buttons). */
	public downloadErrorMessage = '';

	/** Downloads one invoice import error file (saved in processed-invoices). */
	public downloadInvoiceImportErrors(fileName: string) {
		this.downloadingErrorFile = fileName;
		this.downloadErrorMessage = '';
		this.conversionService
			.downloadInvoiceImportErrorFile(fileName)
			.toPromise()
			.then(
				(blob) => {
					this.downloadingErrorFile = null;
					saveDownloadedFile(blob, fileName);
				},
				(error) => {
					this.downloadingErrorFile = null;
					readDownloadError(error, fileName + ' could not be downloaded.').then((message) => (this.downloadErrorMessage = message));
				}
			);
	}

	/** Downloads this carrier's skipped negative invoices as a CSV for the account rep. */
	public downloadSkippedInvoices() {
		const result = this.progress ? this.progress.result : null;
		if (result) {
			downloadSkippedInvoicesCsv(skippedNegativeInvoices([result]), result.clientName || this.clientName);
		}
	}

	/** A step detail split into its notes (the server puts each note on its own line). */
	public detailLines(detail: string): string[] {
		return (detail || '')
			.split('\n')
			.map((line) => line.trim())
			.filter((line) => line.length > 0);
	}

	public formatDuration(ms: number): string {
		if (ms === null || ms === undefined || ms < 0) {
			return '';
		}
		const totalSeconds = Math.floor(ms / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
	}

	public statusClass(status: string): string {
		switch (status) {
			case 'Success':
				return 'text-success';
			case 'Failed':
				return 'text-danger font-weight-bold';
			case 'Running':
				return 'text-primary font-weight-bold';
			default:
				return 'text-muted';
		}
	}

	public statusIcon(status: string): string {
		switch (status) {
			case 'Success':
				return 'fa-circle-check';
			case 'Failed':
				return 'fa-circle-xmark';
			case 'Skipped':
				return 'fa-circle-minus';
			case 'Running':
				return 'fa-spinner fa-spin';
			default:
				return 'fa-circle';
		}
	}

	private get hasResult(): boolean {
		return !!(this.progress && this.progress.result);
	}

	private tick() {
		if (this.isRunning) {
			this.elapsedMs = Date.now() - this.startedAt;
			this.buildStepRows();
			this.poll();
		}
	}

	private poll() {
		if (this.pollInFlight || !this.runId) {
			return;
		}

		this.pollInFlight = true;
		const polledRunId = this.runId;
		this.conversionService
			.getMigrationProgress(polledRunId)
			.toPromise()
			.then(
				(progress: ConversionMigrationProgress) => {
					this.pollInFlight = false;
					// A response for an earlier carrier's run (the batch moved on while it was in
					// flight) must not overwrite the current carrier's steps.
					if (polledRunId !== this.runId) {
						return;
					}
					// A request that has already finished owns the final state.
					if (progress && this.isRunning) {
						this.applyProgress(progress);
					}
				},
				() => {
					this.pollInFlight = false;
				}
			);
	}

	private applyProgress(progress: ConversionMigrationProgress) {
		this.progress = progress;
		this.progressReceivedAt = Date.now();
		if (progress.clientName) {
			this.clientName = progress.clientName;
		}
		this.buildStepRows();
	}

	private applyResult(result: ConversionMigrationResult) {
		const progress = new ConversionMigrationProgress();
		progress.clientName = result.clientName;
		progress.isComplete = true;
		progress.steps = result.steps || [];
		progress.stepStartedOn = {};
		progress.result = result;
		this.applyProgress(progress);
	}

	private complete() {
		this.isRunning = false;
		this.stopTimer();
		this.buildStepRows();
	}

	private stopTimer() {
		if (this.timer) {
			clearInterval(this.timer);
			this.timer = null;
		}
	}

	/** "Part 1", "Part 2" or "Full Conversion" (part 3), for the title. */
	get partLabel(): string {
		return this.part === 3 ? 'Full Conversion' : 'Part ' + this.part;
	}

	private get defaultFirstStep(): number {
		return this.part === 2 ? this.part1LastStep + 1 : 1;
	}

	private get defaultStepNames(): string[] {
		if (this.part === 2) {
			// Part 2 matches the invoices imported by hand instead of posting them.
			const names = this.allStepNames.slice(this.part1LastStep);
			names[0] = 'Match the manually imported invoices';
			return names;
		}
		return this.part === 3 ? this.allStepNames.slice() : this.allStepNames.slice(0, this.part1LastStep);
	}

	private buildStepRows() {
		const progress = this.progress;
		const useServerNames = !!(progress && progress.stepNames && progress.stepNames.length > 0);
		const names = useServerNames ? progress.stepNames : this.defaultStepNames;
		const firstStep =
			useServerNames && progress.firstStep ? progress.firstStep : this.defaultFirstStep;
		const recorded: ConversionMigrationStep[] = progress && progress.steps ? progress.steps : [];
		const finished = !this.isRunning || (progress && progress.isComplete);

		this.stepRows = names.map((name, index) => {
			const stepNumber = firstStep + index;
			const entries = recorded.filter((recordedStep) => recordedStep.step === stepNumber);
			const entry = entries.length > 0 ? entries[entries.length - 1] : null;

			const row = new MigrationProgressStepRow();
			row.step = stepNumber;
			row.action = entry && entry.action ? entry.action : name;

			if (entry) {
				row.status = entry.status;
				row.detail = entry.detail;
			} else if (!finished && progress && progress.currentStep === stepNumber) {
				row.status = 'Running';
				row.detail = progress.activity;
			} else {
				row.status = finished ? 'Not Run' : 'Pending';
				row.detail = '';
			}

			row.durationMs = this.stepDuration(stepNumber, row.status);
			return row;
		});
	}

	private stepDuration(stepNumber: number, status: string): number {
		const progress = this.progress;
		if (!progress || !progress.stepStartedOn) {
			return null;
		}

		const start = this.parseTime(progress.stepStartedOn[stepNumber.toString()]);
		if (start === null) {
			return null;
		}

		if (status === 'Running') {
			// Server time of the last update plus the client time since it arrived, so
			// the clock keeps moving between polls without mixing the two clocks.
			const updated = this.parseTime(progress.updatedOn);
			if (updated === null) {
				return null;
			}
			return updated - start + (Date.now() - this.progressReceivedAt);
		}

		// A step ends where the next recorded step starts, or where the run finished.
		let end: number = null;
		Object.keys(progress.stepStartedOn).forEach((key) => {
			const other = parseInt(key, 10);
			const otherStart = this.parseTime(progress.stepStartedOn[key]);
			if (other > stepNumber && otherStart !== null && (end === null || otherStart < end)) {
				end = otherStart;
			}
		});

		if (end === null) {
			end = this.parseTime(progress.finishedOn);
		}

		return end === null ? null : end - start;
	}

	private parseTime(value: string): number {
		if (!value) {
			return null;
		}
		// The API sends UTC; add the Z if it is missing so the browser does not read it as local.
		const text = /([zZ]|[+-]\d{2}:\d{2})$/.test(value) ? value : value + 'Z';
		const time = new Date(text).getTime();
		return isNaN(time) ? null : time;
	}
}



// WEBPACK FOOTER //
// ./src/app/portal/conversion/migration-progress-modal/migration-progress-modal.component.ts